/**
 * สูตรคำนวณสต็อกฝั่ง client — พอร์ตมาจาก backend ตรง ๆ
 *   microservices/inventory/utils.js  +  microservices/inventory/report.js
 *
 * ทำไมย้ายมาคำนวณฝั่งนี้ได้: สต็อกที่นี่เป็น quantity-only ไม่มีต้นทุน ไม่มี FIFO ไม่มีการตีมูลค่า
 * ทุกสูตรของ server คือผลบวกที่ติดเครื่องหมายมาแล้ว (`balance[id] += tx[id]`)
 * server ไม่เคยลบเอง — เครื่องหมายถูกใส่ตั้งแต่ตอนเขียน (เบิกออกส่งค่าติดลบมา)
 * ผลลัพธ์จึงเท่ากันเป๊ะ และไม่ขึ้นกับลำดับของแถว
 *
 * ไฟล์นี้ต้อง "ไม่ import อะไรเลย" (ไม่เอา lodash/dayjs/vue)
 * เพราะ scripts/test-stock-math.mjs compile ไฟล์นี้ไฟล์เดียวแล้ว import ตรง ๆ โดยไม่ stub อะไร
 */

export interface StockTxRow {
  key2?: string;
  storeId?: string;
  businessDate?: string;
  /** 'pos-closed' = แถวที่ระบบตัดตอนขาย · ที่เหลือเป็น ISO ของรายการที่คนกรอกเอง */
  createdAt?: string;
  type?: string;
  /** stockId -> จำนวนที่ติดเครื่องหมายมาแล้ว */
  tx?: Record<string, number>;
  timestamp?: string;
  employee?: { employeeId?: string; name?: string };
  comments?: Record<string, string>;
  comment?: string;
  note?: string;
  remark?: string;
  closed?: boolean;
  seq?: number;
  sumTotalSoldQty?: number;
}

export type DeltaMap = Record<string, number>;

/**
 * ชนิดที่ถูกนับเข้า END ของรายงานรายวัน
 * ตรงกับ whitelist ใน calculateBeginAndEndOfEachDate ของ server เป๊ะ ๆ — ห้ามเพิ่มเอง
 * ดูหมายเหตุที่ computeItemByDate
 */
export const END_TYPES: readonly string[] = [
  "ADD",
  "SOLD",
  "ADJUST_IN",
  "ADJUST_OUT",
  "WITHDRAW",
];

/** key ประจำแถว = sort key ของ DynamoDB ใช้เป็น key ของ IndexedDB ด้วย */
export const rowKey = (row: StockTxRow): string =>
  row.key2 || `${row.businessDate || ""}:${row.createdAt || ""}`;

export const sortRowsByKey = (rows: StockTxRow[]): StockTxRow[] =>
  [...(rows || [])].sort((a, b) => {
    const ka = rowKey(a);
    const kb = rowKey(b);
    return ka < kb ? -1 : ka > kb ? 1 : 0;
  });

// ---------------------------------------------------------------------------
// ตัวช่วยเทียบวัน
//
// server เทียบ businessDate แบบ string ดิบ ๆ (report.js)
//   tx.businessDate < startDate            -> ยอดยกมา
//   tx.businessDate >= startDate && <= endDate  -> ในช่วง
// แถวที่ไม่มี businessDate จึงตกทั้งสองฝั่ง เพราะ undefined เทียบกับ string ได้ false เสมอ
// ห้ามใส่ ?? '' ให้มัน ไม่งั้นแถวพิการจะไปโผล่ในยอดยกมา แล้วตัวเลขจะไม่ตรงกับ server
// ---------------------------------------------------------------------------

const isBeforeDate = (row: StockTxRow, startDate: string): boolean =>
  typeof row.businessDate === "string" && row.businessDate < startDate;

const isWithinDate = (
  row: StockTxRow,
  startDate: string,
  endDate: string
): boolean =>
  typeof row.businessDate === "string" &&
  row.businessDate >= startDate &&
  row.businessDate <= endDate;

/** แถวที่อยู่ในช่วงวันทำการ — ใช้แทนการยิง GET /inventories-tx/{start}/{end} */
export const selectRowsInRange = (
  rows: StockTxRow[],
  startDate: string,
  endDate: string
): StockTxRow[] =>
  (rows || []).filter((row) => isWithinDate(row, startDate, endDate));

// ---------------------------------------------------------------------------
// สูตรหลัก
// ---------------------------------------------------------------------------

/**
 * ยอดคงเหลือปัจจุบัน — ตรงกับ utils.calculateBalance
 * บวกล้วน ไม่มีการดูชนิด ไม่มีการลบ จึงไม่ขึ้นกับลำดับแถว
 *
 * ห้ามปัดเศษระหว่างสะสม สูตรตัดสต็อกใช้ทศนิยม (เช่น 0.05 กก./แก้ว)
 * การบวก float ขึ้นกับลำดับ ปัดตอนแสดงผลด้วย roundQty เท่านั้น
 */
export const computeBalance = (rows: StockTxRow[]): DeltaMap => {
  const balance: DeltaMap = {};
  for (const row of rows || []) {
    const tx = row.tx || {};
    for (const stockId of Object.keys(tx)) {
      balance[stockId] = (balance[stockId] || 0) + Number(tx[stockId] || 0);
    }
  }
  return balance;
};

/**
 * เวลาที่สต็อกแต่ละตัวขยับล่าสุด (epoch ms) — ตรงกับ utils.calcuateLastUpdate
 *
 * ของ server ใช้ _.orderBy(..., 'timestamp', 'asc') แล้ว dayjs(timestamp).valueOf()
 * ซึ่งมีพฤติกรรมสองอย่างที่ต้องลอกมา ไม่ใช่ "แก้ให้ดีขึ้น" ไม่งั้นตัวเลขไม่ตรงกัน
 *   1. แถวที่ไม่มี timestamp ถูกจัดไปท้ายสุด (ลodash วาง undefined ไว้หลัง)
 *   2. dayjs(undefined) = เวลาปัจจุบัน แถวพิการจึงชนะและรายงานว่า "เพิ่งอัปเดต"
 * ผลของฟังก์ชันนี้จึงไม่ deterministic โดยธรรมชาติ — อย่าเอาไป assert ตรง ๆ ในเทสต์/แท็บ Verify
 */
export const computeLastUpdate = (
  rows: StockTxRow[]
): Record<string, number> => {
  const decorated = (rows || []).map((row, index) => ({ row, index }));

  decorated.sort((a, b) => {
    const ta = a.row.timestamp;
    const tb = b.row.timestamp;
    const aHas = ta !== undefined && ta !== null;
    const bHas = tb !== undefined && tb !== null;
    if (aHas !== bHas) return aHas ? -1 : 1; // ไม่มีค่า -> ท้ายสุด
    if (aHas && bHas && ta !== tb) return (ta as string) < (tb as string) ? -1 : 1;
    return a.index - b.index; // เท่ากันให้คงลำดับเดิม (lodash sort แบบ stable)
  });

  const lastUpdate: Record<string, number> = {};
  for (const { row } of decorated) {
    const at =
      row.timestamp === undefined ? Date.now() : Date.parse(String(row.timestamp));
    const tx = row.tx || {};
    for (const stockId of Object.keys(tx)) {
      lastUpdate[stockId] = at;
    }
  }
  return lastUpdate;
};

/**
 * รายงานรวมตามช่วงวัน — ตรงกับ report.reportSummaryRange
 * คืน { BEGIN, ...เฉพาะชนิดที่มีจริงในช่วง, END }
 *
 * END = ยอดคงเหลือ ณ endDate (ยกมา + ในช่วง) ไม่ใช่ยอดปัจจุบัน
 */
export const computeSummaryRange = (
  rows: StockTxRow[],
  startDate: string,
  endDate: string
): Record<string, DeltaMap> => {
  const before: StockTxRow[] = [];
  const inRange: StockTxRow[] = [];

  for (const row of rows || []) {
    if (isBeforeDate(row, startDate)) before.push(row);
    else if (isWithinDate(row, startDate, endDate)) inRange.push(row);
  }

  // เท่ากับ utils.calcualteByTypeAndProductId : { ชนิด: { stockId: ยอดรวม } }
  const byType: Record<string, DeltaMap> = {};
  for (const row of inRange) {
    const type = String(row.type || "");
    const tx = row.tx || {};
    for (const stockId of Object.keys(tx)) {
      if (!byType[type]) byType[type] = {};
      byType[type][stockId] =
        (byType[type][stockId] || 0) + Number(tx[stockId] || 0);
    }
  }

  return {
    BEGIN: computeBalance(before),
    ...byType,
    END: computeBalance([...before, ...inRange]),
  };
};

export interface ItemByDateRow {
  date: string;
  BEGIN: number;
  END: number;
  [type: string]: number | string;
}

/**
 * รายงานรายวันของสต็อก 1 ตัว — ตรงกับ report.reportItemBydate
 *
 * ข้อจำกัดของ server 3 อย่างที่ "จงใจลอกมา" ไม่ใช่เขียนพลาด
 * ถ้าแก้ให้ถูกตามสามัญสำนึก ตัวเลขจะไม่ตรงกับที่ลูกค้าเห็นในระบบเดิมทันที
 *   1. คำนวณ BEGIN/END จากประวัติ "ทั้งหมด" ก่อน แล้วค่อยตัดช่วงทีหลัง
 *      (ถ้ากรองก่อน ยอดยกมาของวันแรกในช่วงจะกลายเป็น 0)
 *   2. ออกเฉพาะวันที่มีความเคลื่อนไหว วันเงียบไม่มีแถว
 *   3. END บวกจาก END_TYPES เท่านั้น ชนิดแปลก ๆ จะโผล่เป็นคอลัมน์แต่ไม่ขยับ END
 */
export const computeItemByDate = (
  rows: StockTxRow[],
  stockId: string,
  startDate?: string | null,
  endDate?: string | null
): ItemByDateRow[] => {
  // { วันทำการ: { ชนิด: ยอด } } ของสต็อกตัวนี้ จากประวัติทั้งหมด
  const byDate: Record<string, Record<string, number>> = {};
  for (const row of rows || []) {
    const tx = row.tx || {};
    if (!(stockId in tx)) continue;
    // server ใช้ businessDate เป็น key ตรง ๆ แถวที่ไม่มีค่าจึงกลายเป็น key "undefined"
    const date = String(row.businessDate);
    const type = String(row.type || "");
    if (!byDate[date]) byDate[date] = {};
    byDate[date][type] = (byDate[date][type] || 0) + Number(tx[stockId] || 0);
  }

  const dates = Object.keys(byDate).sort();

  let running = 0;
  let result: ItemByDateRow[] = dates.map((date) => {
    const line = byDate[date];
    const begin = running;
    let moved = 0;
    for (const type of END_TYPES) moved += line[type] || 0;
    running = begin + moved;
    return { ...line, date, BEGIN: begin, END: running } as ItemByDateRow;
  });

  if (startDate) result = result.filter((line) => line.date >= startDate);
  if (endDate) result = result.filter((line) => line.date <= endDate);
  return result;
};

/** ปัดเศษตอนแสดงผลเท่านั้น ห้ามเรียกระหว่างสะสมยอด */
export const roundQty = (n: number, digits = 6): number =>
  Number(Number(n || 0).toFixed(digits));
